import { useMemo } from 'react';
import { getAllSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import type { Word } from '@goldcast/player-config/src/types/transcript-types';
import { parseTranscriptToParagraphs } from './utils';
import type { ChapterParagraph } from './types';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';

export function useChapterParagraphs(): ChapterParagraph[] {
  const transcriptStore = useTranscriptContext();
  const { clipData } = useClipsContext();

  const allSegments = useMemo(() => getAllSegments(clipData), [clipData]);

  const transcript = useMemo(() => {
    return allSegments.reduce<Record<string, Word[]>>((acc, segment) => {
      acc[segment.id] = segment.type === SegmentType.COMPOSITE ? transcriptStore.transcript : [];
      return acc;
    }, {});
  }, [transcriptStore.transcript, allSegments]);

  return useMemo(() => {
    if (!transcriptStore.isTranscriptInitialized) {
      return [];
    }

    const paragraphs = parseTranscriptToParagraphs(
      transcript,
      transcriptStore.speakersWithDetails,
      clipData,
      transcriptStore.chapterData ?? []
    );

    return paragraphs.filter(p => !!p.chapterDetails).map(p => ({ ...p.chapterDetails!, segmentId: p.segmentId }));
  }, [
    transcript,
    transcriptStore.isTranscriptInitialized,
    transcriptStore.speakersWithDetails,
    transcriptStore.chapterData,
    clipData
  ]);
}
